import { useCallback, useEffect, useState, useContext, useMemo } from "react";

import { CartContext } from "@cart-client/contexts/CartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { CartItemWidgetReposition, CartItemWidgetRepositionType, CartItemWithUUID, FilterCakeItems } from "@swiggy-private/cart-types";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import {
    clickAddCakeMessagePill,
    clickCloseBottomHalfCard,
    clickCloseBottomAddMsgHalfCard,
    impressionAddCakeMessagePill,
    impressionBottomHalfCard,
    impressionMessageBottomHalfCard,
} from "@cart-client/analytics/events/CartItemPersonalised";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { AddCakeMessageWidgetType } from "@cart-client/interfaces/Cart";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { DEFAULT_VALUES, ItemTags } from "./constants";
import { isEmpty } from "@swiggy-private/js-utils";
import { getCartItemsFromResponse } from "@cart-client/helpers/Cart";
import { createCartItemListWithUUID, getItemsListForCartUpdate } from "@cart-client/helpers/CartItemRenderer";
import { isGiftingCartSource, fetchGiftingCartItemsFromResponse } from "@cart-client/helpers/GiftingCart";
import { isItemAvailable } from "@swiggy-private/cart-ui";
import { effectiveServerCartComputer, isMROFlow } from "@cart-client/helpers/MROFoodCart";

export const useAddCakeMessage = (restaurantId?: string | number) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { doUpdateCart } = useContext(UpdateCartContext);
    const { data: cartFlowData } = useContext(CartFlowContext);

    const { localCart: parentLocalCart, reviewedCart: parentCartResponse } = cartState;

    const cartResponse = useMemo(
        () => effectiveServerCartComputer(parentCartResponse, isMROFlow(), restaurantId),
        [parentCartResponse, restaurantId],
    );

    const cartId = cartResponse?.cartId;
    const originalCartId = cartResponse?.originalCartId;

    const [cakeMessageHalfCardState, setCakeMessageHalfCardState] = useState({
        showSelectCakeHalfCard: false,
        showAddCakeMessageHalfCard: false,
    });
    const [isEditState, setIsEditState] = useState<boolean>(false);
    const [isScrolled, setIsScrolled] = useState<boolean>(false);
    const [pillText, setPillText] = useState<string>("");
    const cartMenuItemsList = useMemo(() => {
        const cartItems = getCartItemsFromResponse(cartResponse);
        if (!isEmpty(cartItems)) {
            return cartItems;
        } else if (isGiftingCartSource(cartFlowData?.cartSource)) {
            return fetchGiftingCartItemsFromResponse(parentCartResponse as GiftingReviewedCart);
        }
        return [];
    }, [cartFlowData?.cartSource, cartResponse, parentCartResponse]);
    const updatedCartItems = useMemo(() => createCartItemListWithUUID(cartMenuItemsList), [cartMenuItemsList]);

    useEffect(() => {
        setPillText(isEditState ? AddCakeMessageWidgetType.EDIT : AddCakeMessageWidgetType.ADD);
    }, [isEditState]);

    const cakeMessageWidget = useMemo(() => {
        return cartResponse?.cartItemWidgetReposition?.find(
            (item) => item.type === CartItemWidgetRepositionType.ADD_MESSAGE_ON_CAKE,
        ) as CartItemWidgetReposition | undefined;
    }, [cartResponse]);

    const showAddCakeMessagePill = !!cakeMessageWidget;

    const PMMBanner = useMemo(() => {
        return showAddCakeMessagePill && !isEmpty(cakeMessageWidget?.config?.PMM_widget)
            ? {
                  arrowColor: cakeMessageWidget?.config?.PMM_widget?.arrow_color,
                  imageId: cakeMessageWidget?.config?.PMM_widget?.image_id,
              }
            : {};
    }, [showAddCakeMessagePill, cakeMessageWidget]);
    const maxNumberOfCakesAllowed = useMemo(
        () => cakeMessageWidget?.config?.max_number_of_cakes_allowed || DEFAULT_VALUES.MAX_NUMBER_OF_CAKES_ALLOWED,
        [cakeMessageWidget],
    );

    const maxCharactersPerCakeMessage = useMemo(
        () =>
            cakeMessageWidget?.config?.max_characters_limit_per_cake_message ||
            DEFAULT_VALUES.MAX_CHARACTERS_PER_CAKE_MESSAGE,
        [cakeMessageWidget],
    );

    const predefinedWordsForCakeMessage = useMemo(
        () =>
            cakeMessageWidget?.config?.predefined_words_for_cake_message ||
            DEFAULT_VALUES.PREDEFINED_WORDS_FOR_CAKE_MESSAGE,
        [cakeMessageWidget],
    );

    const filterCakeItems: FilterCakeItems[] = useMemo(() => {
        // Filter and sort cake items
        const cakeItems = updatedCartItems.filter(
            (item) => isItemAvailable(item) && item.itemTags?.includes(ItemTags.CAKE_MESSAGE_ON_CAKE),
        );

        const expandedCakeItems = cakeItems
            .map((item) => {
                if (isEmpty(item.quantity) || item.quantity <= 1) {
                    return [{ ...item, finalPrice: item.final_price, index: 0 }];
                }
                return Array(item.quantity)
                    .fill(0)
                    .map((_, index: number) => ({
                        ...item,
                        quantity: 1,
                        finalPrice: item.final_price / item.quantity,
                        index,
                    }));
            })
            .reduce((acc, val) => acc.concat(val), []);

        const sortedCakeItems = [...expandedCakeItems].sort((a, b) => {
            const priceA = a.finalPrice;
            const priceB = b.finalPrice;
            return priceB - priceA;
        });
        const slicedItems =
            sortedCakeItems.length <= maxNumberOfCakesAllowed
                ? sortedCakeItems
                : sortedCakeItems.slice(0, maxNumberOfCakesAllowed);
        const menuItemIdCount: Record<string, number> = {};
        slicedItems.forEach((item) => {
            menuItemIdCount[item.menu_item_id] = (menuItemIdCount[item.menu_item_id] || 0) + 1;
        });
        const menuItemIdCurrentIndex: Record<string, number> = {};
        return slicedItems.map((item) => {
            const count = menuItemIdCount[item.menu_item_id];
            if (count > 1) {
                menuItemIdCurrentIndex[item.menu_item_id] = (menuItemIdCurrentIndex[item.menu_item_id] || 0) + 1;
                const currentIndex = menuItemIdCurrentIndex[item.menu_item_id];
                return {
                    ...item,
                    name: `${item.name}`,
                    currentIndex,
                };
            }
            return item;
        });
    }, [updatedCartItems, maxNumberOfCakesAllowed]);

    const getIsEditState = useCallback(() => {
        const savedMessageExist = filterCakeItems.some(
            (obj) =>
                Array.isArray(obj?.customer_request_messages) &&
                obj.customer_request_messages.some((msg) => Boolean(msg)),
        );

        setIsEditState(savedMessageExist);
    }, [filterCakeItems]);

    const cakeItemIds = filterCakeItems.map((item) => item?.menu_item_id || "") || [];
    const context = JSON.stringify({ cartId, originalCartId, cakeItemIds });

    const onAddCakeMessagePillImpression = useCallback(() => {
        impressionAddCakeMessagePill(impressionEvent, context, pillText);
    }, [impressionEvent, context, pillText]);

    const onAddCakeMessagePillClick = useCallback(() => {
        clickAddCakeMessagePill(clickEvent, context, pillText);
    }, [clickEvent, context, pillText]);

    const handleAddCakeMessageClick = useCallback(() => {
        onAddCakeMessagePillClick();
        if (isEmpty(filterCakeItems)) {
            setCakeMessageHalfCardState({
                showSelectCakeHalfCard: false,
                showAddCakeMessageHalfCard: false,
            });
        } else if (filterCakeItems.length === 1) {
            setCakeMessageHalfCardState({
                showSelectCakeHalfCard: false,
                showAddCakeMessageHalfCard: true,
            });
        } else {
            setCakeMessageHalfCardState({
                showSelectCakeHalfCard: true,
                showAddCakeMessageHalfCard: false,
            });
        }
    }, [filterCakeItems, onAddCakeMessagePillClick]);

    useEffect(() => {
        getIsEditState();
    }, [cartResponse, getIsEditState]);

    const buildCartItemsUpdatePayload = useCallback(
        (cartItems: CartItemWithUUID[]): { cartItems?: ReturnType<typeof getItemsListForCartUpdate> } =>
            isMROFlow() ? {} : { cartItems: getItemsListForCartUpdate(cartItems) },
        [],
    );

    const submitCakeMessage = useCallback(
        (unId: string, messageArray: string[]) => {
            const finalCartItems = updatedCartItems.map((item) => {
                // to differentiate between the same cake but different variant
                if (item.unId === unId) {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    return { ...item, customer_request_messages: messageArray };
                }
                return item;
            });

            setCakeMessageHalfCardState({
                showSelectCakeHalfCard: false,
                showAddCakeMessageHalfCard: false,
            });
            getIsEditState();

            doUpdateCart({
                ...parentLocalCart,
                ...buildCartItemsUpdatePayload(finalCartItems), // to update the cart with only required local cart fields
            }).catch((_) => {
                // do nothing
            });
        },
        [updatedCartItems, parentLocalCart, doUpdateCart, getIsEditState, buildCartItemsUpdatePayload],
    );

    const onHalfCardImpression = useCallback(() => {
        if (cakeMessageHalfCardState.showSelectCakeHalfCard) {
            impressionBottomHalfCard(impressionEvent, context, pillText);
        }
    }, [cakeMessageHalfCardState.showSelectCakeHalfCard, impressionEvent, context, pillText]);

    const onMessageHalfCardImpression = useCallback(() => {
        if (cakeMessageHalfCardState.showAddCakeMessageHalfCard) {
            impressionMessageBottomHalfCard(impressionEvent, context, "cake half card");
        }
    }, [cakeMessageHalfCardState.showAddCakeMessageHalfCard, impressionEvent, context]);

    const onHalfCardClick = useCallback(() => {
        clickCloseBottomHalfCard(clickEvent, context, pillText);
    }, [clickEvent, context, pillText]);

    const onMessageHalfCardClick = useCallback(() => {
        clickCloseBottomAddMsgHalfCard(clickEvent, context, pillText);
    }, [clickEvent, context, pillText]);

    return {
        showAddCakeMessagePill,
        getIsEditState,
        isEditState,
        handleAddCakeMessageClick,
        cakeMessageHalfCardState,
        setCakeMessageHalfCardState,
        filterCakeItems,
        PMMBanner,
        maxCharactersPerCakeMessage,
        predefinedWordsForCakeMessage,
        isScrolled,
        setIsScrolled,
        submitCakeMessage,
        onAddCakeMessagePillImpression,
        onAddCakeMessagePillClick,
        onHalfCardImpression,
        onMessageHalfCardImpression,
        onMessageHalfCardClick,
        onHalfCardClick,
        pillText,
    };
};
